import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Button
} from '@mui/material';
import {
  TrendingUp as RevenueIcon,
  TrendingDown as ExpenseIcon,
  AccountBalance as CashIcon,
  Receipt as VATIcon,
  Business as ComplianceIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  EmojiEvents as TrophyIcon,
  Lightbulb as IdeaIcon
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Confetti from 'react-confetti';
import axios from 'axios';
import { dashboardApi, getErrorMessage } from '../../api/services';

interface DashboardMetrics {
  total_revenue: number;
  total_expenses: number;
  net_profit: number;
  profit_margin: number;
  cash_balance: number;
  accounts_receivable: number;
  accounts_payable: number;
  vat_liability: number;
  corporation_tax_due: number;
  monthly_revenue_growth: number;
  customer_count: number;
  vat_return_due_date: string;
  companies_house_due_date: string;
}

interface CashFlowData {
  month: string;
  income: number;
  expenses: number;
  net_flow: number;
}

const FinancialDashboard: React.FC = () => {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [cashFlowData, setCashFlowData] = useState<CashFlowData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Phase 2: Gamification State
  const [dailyPulse, setDailyPulse] = useState<any>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });

  useEffect(() => {
    const handleResize = () => setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      const [metricsData, cashFlowData] = await Promise.all([
        dashboardApi.getMetrics(),
        dashboardApi.getCashFlow()
      ]);

      setMetrics(metricsData);
      setCashFlowData(cashFlowData);

      // Fetch PROACTIVE Daily Pulse
      try {
        const pulseRes = await axios.get('/api/cfo/daily-standup/test-customer-123');
        setDailyPulse(pulseRes.data);
      } catch (e) {
        console.log("Daily pulse fetch failed, using default");
      }

      // Check for Success Trigger (Mock: if profit > 150k, celebrate!)
      if (metricsData.net_profit > 150000) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 8000);
      }

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);

      // Fallback to mock data for demo
      setMetrics({
        total_revenue: 485750.00,
        total_expenses: 312450.00,
        net_profit: 173300.00,
        profit_margin: 35.7,
        cash_balance: 125680.50,
        accounts_receivable: 89450.00,
        accounts_payable: 45230.00,
        vat_liability: 28950.00,
        corporation_tax_due: 32826.00,
        monthly_revenue_growth: 12.5,
        customer_count: 47,
        vat_return_due_date: "2024-02-07",
        companies_house_due_date: "2024-06-15"
      });

      setCashFlowData([
        { month: 'Sep 2023', income: 35000, expenses: 23000, net_flow: 12000 },
        { month: 'Oct 2023', income: 38500, expenses: 25200, net_flow: 13300 },
        { month: 'Nov 2023', income: 42100, expenses: 26800, net_flow: 15300 },
        { month: 'Dec 2023', income: 45200, expenses: 28100, net_flow: 17100 },
        { month: 'Jan 2024', income: 41800, expenses: 26500, net_flow: 15300 }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!metrics) return null;

  const expenseData = [
    { name: 'Salaries', value: 156000, color: '#8884d8' },
    { name: 'Office Rent', value: 48000, color: '#82ca9d' },
    { name: 'Professional Fees', value: 24500, color: '#ffc658' },
    { name: 'Marketing', value: 18750, color: '#ff7300' },
    { name: 'Other', value: 65200, color: '#00ff88' }
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      {/* Header - The Daily Pulse */}
      {showConfetti && <Confetti width={windowSize.width} height={windowSize.height} recycle={false} />}

      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            {dailyPulse ? dailyPulse.greeting : "Financial Dashboard"}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {dailyPulse ? dailyPulse.summary_text.split('.')[0] + '.' : "Overview of your financial performance"}
            {dailyPulse && <Chip label="Insight" size="small" color="primary" icon={<IdeaIcon />} sx={{ ml: 1 }} />}
          </Typography>
        </Box>
        {dailyPulse && process.env.NODE_ENV === 'development' && (
          <Button variant="outlined" startIcon={<TrophyIcon />} onClick={() => setShowConfetti(true)}>
            Celebration Mode
          </Button>
        )}
      </Box>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Revenue
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(metrics.total_revenue)}
                  </Typography>
                  <Chip
                    label={`+${metrics.monthly_revenue_growth}%`}
                    color="success"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Box>
                <RevenueIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Net Profit
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(metrics.net_profit)}
                  </Typography>
                  <Chip
                    label={`${metrics.profit_margin}% margin`}
                    color="success"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Box>
                <RevenueIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Cash Balance
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(metrics.cash_balance)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Available funds
                  </Typography>
                </Box>
                <CashIcon color="info" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    VAT Liability
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(metrics.vat_liability)}
                  </Typography>
                  <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                    Due {formatDate(metrics.vat_return_due_date)}
                  </Typography>
                </Box>
                <VATIcon color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Card data-testid="cash-flow" className="cash-flow">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Cash Flow Trend
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={cashFlowData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                  <Line type="monotone" dataKey="income" stroke="#8884d8" name="Income" />
                  <Line type="monotone" dataKey="expenses" stroke="#82ca9d" name="Expenses" />
                  <Line type="monotone" dataKey="net_flow" stroke="#ffc658" name="Net Flow" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Expense Breakdown
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={expenseData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {expenseData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* UK Compliance Status */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ComplianceIcon />
                UK Compliance Status
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="VAT Returns"
                    secondary={`Next due: ${formatDate(metrics.vat_return_due_date)}`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Companies House"
                    secondary={`Next due: ${formatDate(metrics.companies_house_due_date)}`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ScheduleIcon color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Corporation Tax"
                    secondary={`Estimated: ${formatCurrency(metrics.corporation_tax_due)}`}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Stats
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Total Customers:</Typography>
                  <Typography fontWeight="bold">{metrics.customer_count}</Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Accounts Receivable:</Typography>
                  <Typography fontWeight="bold">{formatCurrency(metrics.accounts_receivable)}</Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Accounts Payable:</Typography>
                  <Typography fontWeight="bold">{formatCurrency(metrics.accounts_payable)}</Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Monthly Growth:</Typography>
                  <Chip
                    label={`+${metrics.monthly_revenue_growth}%`}
                    color="success"
                    size="small"
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FinancialDashboard;
